import { isEqual, isValid, set, isBefore, isAfter, addYears, differenceInSeconds, parseISO, format } from "date-fns";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
const useFormat = (defaultLocale) => {
  const { locale } = useTheme();
  function format$1(date, formatStr = "PP") {
    const normalizedDate = typeof date === "string" ? parseISO(date) : date;
    return format(normalizedDate, formatStr, {
      locale: defaultLocale || locale
    });
  }
  return { format: format$1 };
};
function getValueFromInputValue(inputValue, isTimeIncluded) {
  let value;
  if (isDateRange(inputValue)) {
    value = {
      from: getDate(inputValue.from, isTimeIncluded)
    };
    if (inputValue.to) {
      value.to = getDate(inputValue.to, isTimeIncluded);
    }
  } else {
    value = getDate(inputValue, isTimeIncluded);
  }
  return value;
}
function getDate(date, isTimeIncluded = true) {
  let newDate = date || /* @__PURE__ */ new Date();
  if (!isValid(newDate)) {
    throw new Error("Invalid input date. Expecting a native javascript date object, but got: ".concat({}.toString.call(date), "."));
  }
  newDate = set(newDate, { seconds: 0, milliseconds: 0 });
  if (!isTimeIncluded) {
    newDate = set(newDate, { hours: 0, minutes: 0 });
  }
  return newDate;
}
function getRangesFromTimes(times, isTimeIncluded) {
  return times.map(({ from: propFrom, to: propTo, ...rest }) => {
    const from = getDate(propFrom, isTimeIncluded);
    const to = propTo && getDate(propTo, isTimeIncluded);
    throwIfInvalidRange(from, to, false);
    return to && !isEqual(from, to) ? { from, to, ...rest } : { from, ...rest };
  });
}
function getRange(props, times, currentDate, isTimeIncluded) {
  let from;
  let to;
  if (props.range) {
    from = getDate(props.range.from, isTimeIncluded);
    to = getDate(props.range.to, isTimeIncluded);
  }
  if (times) {
    const earliestTimeFrom = times.reduce(
      (earliest, time) => earliest && isBefore(earliest, time.from) ? earliest : time.from,
      void 0
    );
    let latestTimeTo = times.reduce(
      (latest, time) => latest && isAfter(latest, time.to || time.from) ? latest : time.to || time.from,
      void 0
    );
    if (!from || earliestTimeFrom && isAfter(earliestTimeFrom, from)) {
      from = earliestTimeFrom;
    }
    if (!to || latestTimeTo && isBefore(latestTimeTo, to)) {
      to = latestTimeTo;
    }
  }
  if (!from) {
    from = currentDate;
  }
  if (!to) {
    to = addYears(currentDate, 6);
  }
  from = set(from, { seconds: 0, milliseconds: 0 });
  to = set(to, { seconds: 0, milliseconds: 0 });
  if (!isTimeIncluded) {
    from = set(from, { hours: 0, minutes: 0 });
    to = set(to, { hours: 0, minutes: 0 });
  }
  throwIfInvalidRange(from, to);
  return { from, to };
}
function throwIfInvalidRange(from, to, mustHaveLength = true) {
  if (!isValid(from)) {
    throw new Error('Invalid date/time range "from" value: "'.concat(from, '". Expected a javascript Date object.'));
  }
  if (to && !isValid(to)) {
    throw new Error('Invalid date/time range "to" value: "'.concat(to, '". Expected a javascript Date object.'));
  }
  if (mustHaveLength && isValid(from) && isValid(to) && to && isAfter(from, to)) {
    throw new Error('Invalid date range values. "From": "'.concat(from, '" must be before "to": "').concat(to, '".'));
  }
}
function getClosestDateInRange(date, times) {
  let closestDate;
  let latestDate;
  let lastDiff = Infinity;
  for (const { from, to } of times) {
    const fromDiff = Math.abs(differenceInSeconds(date, from));
    const toDiff = to ? Math.abs(differenceInSeconds(date, to)) : Infinity;
    const diff = Math.abs(fromDiff < 0 ? fromDiff : toDiff);
    if (diff < lastDiff && isAfter(from, date)) {
      closestDate = from;
      lastDiff = diff;
    }
    if (!latestDate || isAfter(from, latestDate)) {
      latestDate = from;
    }
  }
  return closestDate || latestDate || date;
}
function getDateFromValue(value, currentDate) {
  if (value) {
    return isDateRange(value) ? value.from : value;
  }
  return currentDate;
}
function getDefaultViewDate(value, times, currentDate) {
  const date = getDateFromValue(value, currentDate);
  return getClosestDateInRange(date, times);
}
function isDateRange(obj) {
  if (obj === null || obj instanceof Date) {
    return false;
  }
  const filteredObj = {
    from: obj["from"],
    ...obj["to"] && { to: obj["to"] }
  };
  const keys = Object.keys(filteredObj);
  const keysMatch = keys.length === 1 && keys.includes("from") || keys.length === 2 && keys.includes("from") && keys.includes("to");
  return keysMatch && Object.values(filteredObj).every((value) => value instanceof Date);
}
function isEqualRange(a, b) {
  return isEqual(a.from, b.from) && (!a.to && !b.to || !!a.to && !!b.to && isEqual(a.to, b.to));
}
function isEqualValue(a, b, isTimeIncluded = true) {
  if (!a || !b) {
    return !a && !b;
  }
  const aDateValue = getValueFromInputValue(a, isTimeIncluded);
  const bDateValue = getValueFromInputValue(b, isTimeIncluded);
  if (isDateRange(aDateValue) && !isDateRange(bDateValue) || isDateRange(bDateValue) && !isDateRange(aDateValue)) {
    return false;
  }
  if (isDateRange(aDateValue) && isDateRange(bDateValue)) {
    return isEqualRange(aDateValue, bDateValue);
  } else {
    return isEqual(aDateValue, bDateValue);
  }
}
export {
  getClosestDateInRange,
  getDate,
  getDateFromValue,
  getDefaultViewDate,
  getRange,
  getRangesFromTimes,
  getValueFromInputValue,
  isDateRange,
  isEqualRange,
  isEqualValue,
  throwIfInvalidRange,
  useFormat
};
